import {
    type IncomeSplitExecution,
    IncomeSplitExecutionDetails,
    IncomeSplitExecutionRuleSets,
    IncomeSplitExecutions,
    type IncomeSplitOverviewData,
    type IncomeSplitOverviewHistory,
    type IncomeSplitProgress,
    IncomeSplitProgressOverview,
} from '@fruitful/income-split';
import {
    type IncomeSplitWidgetMode,
    type IncomeSplitWidgetProps,
} from '@fruitful/types';
import React, { useMemo, useState } from 'react';
import {
    BodySmallRegular,
    H5Emphasized,
    Sheet,
    Spinner,
    YStack,
} from 'smoothie';

type OnPressExecution = (execution: IncomeSplitExecution) => void;

interface ResolvedExecution {
    month: IncomeSplitOverviewHistory;
    execution: IncomeSplitExecution;
    /** Index of the execution within its month (chronological order). */
    indexInMonth: number;
}

/** Most recent execution across all months (last execution of the latest month with any). */
const resolveLatestExecution = (
    data: IncomeSplitOverviewData,
): ResolvedExecution | null => {
    for (const month of [...data.history].reverse()) {
        if (month != null && month.executions.length > 0) {
            return {
                month,
                execution: month.executions[
                    month.executions.length - 1
                ] as IncomeSplitExecution,
                indexInMonth: month.executions.length - 1,
            };
        }
    }
    return null;
};

const resolveExecutionById = (
    data: IncomeSplitOverviewData,
    executionId: string,
): ResolvedExecution | null => {
    for (const month of data.history) {
        const indexInMonth = month.executions.findIndex(
            (execution) => execution.id === executionId,
        );
        if (indexInMonth !== -1) {
            return {
                month,
                execution: month.executions[
                    indexInMonth
                ] as IncomeSplitExecution,
                indexInMonth,
            };
        }
    }
    return null;
};

const resolveMonth = (
    data: IncomeSplitOverviewData,
    monthKey?: string,
): IncomeSplitOverviewHistory | null => {
    if (monthKey != null && monthKey !== '') {
        const match = data.history.find((month) => month.month === monthKey);
        if (match != null) {
            return match;
        }
    }
    for (let i = data.history.length - 1; i >= 0; i--) {
        const month = data.history[i];
        if (month != null && month.executions.length > 0) {
            return month;
        }
    }
    return data.history[data.history.length - 1] ?? null;
};

/** Cumulative month progress through the execution just before the given one. */
const previousExecutionProgress = (
    resolved: ResolvedExecution,
): IncomeSplitProgress | undefined =>
    resolved.indexInMonth > 0
        ? resolved.month.executions[resolved.indexInMonth - 1]?.progress
        : undefined;

/**
 * Single-deposit detail: rings (lighter = month progress before this deposit,
 * darker = this deposit's contribution) plus the account-by-account breakdown.
 * Reuses the income-split overview presentational components.
 */
const DepositDetail = ({
    data,
    resolved,
}: {
    data: IncomeSplitOverviewData;
    resolved: ResolvedExecution;
}) => {
    const { execution } = resolved;

    return (
        <YStack gap={16} width="100%" padding={16}>
            <YStack gap={8}>
                <IncomeSplitProgressOverview
                    progress={execution.progress}
                    previousProgress={previousExecutionProgress(resolved)}
                    thisExecutionProgress={execution.thisExecutionProgress}
                />
                <IncomeSplitExecutionDetails execution={execution} />
            </YStack>
            <IncomeSplitExecutionRuleSets
                incomeSplitOverview={data}
                execution={execution}
            />
        </YStack>
    );
};

const IncomeSplitMonthView = ({
    data,
    month,
    onPressExecution,
}: {
    data: IncomeSplitOverviewData;
    month: IncomeSplitOverviewHistory;
    onPressExecution?: OnPressExecution;
}) => {
    const [selected, setSelected] = useState<ResolvedExecution | null>(null);

    const ringsProgress =
        month.progress ??
        (month.executions.length > 0
            ? month.executions[month.executions.length - 1]?.progress
            : undefined);

    return (
        <>
            {ringsProgress != null && (
                <YStack padding={16}>
                    <IncomeSplitProgressOverview progress={ringsProgress} />
                </YStack>
            )}
            <IncomeSplitExecutions
                month={month}
                onPressExecution={onPressExecution}
                onSelectExecution={(execution, indexInMonth) => {
                    setSelected({ month, execution, indexInMonth });
                }}
            />
            <Sheet
                isOpen={selected != null}
                onClose={() => {
                    setSelected(null);
                }}
                showCloseButton
                title={selected?.execution.description ?? 'Deposit details'}
            >
                {selected != null && (
                    <DepositDetail data={data} resolved={selected} />
                )}
            </Sheet>
        </>
    );
};

const WidgetMessage = ({ message }: { message: string }) => (
    <YStack width="100%" padding={16} alignItems="center">
        <BodySmallRegular color="$gray1000" textAlign="center">
            {message}
        </BodySmallRegular>
    </YStack>
);

interface IncomeSplitWidgetDataProps {
    data: IncomeSplitOverviewData | null | undefined;
    isLoading?: boolean;
    isError?: boolean;
    onPressExecution?: OnPressExecution;
}

interface IncomeSplitWidgetBodyProps extends IncomeSplitWidgetDataProps {
    mode: IncomeSplitWidgetMode;
    month?: string;
    executionId?: string;
}

const IncomeSplitWidgetBody = ({
    mode,
    month,
    executionId,
    data,
    isLoading = false,
    isError = false,
    onPressExecution,
}: IncomeSplitWidgetBodyProps) => {
    const resolvedDeposit = useMemo(() => {
        if (data == null || mode !== 'deposit') {
            return null;
        }
        return executionId != null && executionId !== ''
            ? resolveExecutionById(data, executionId)
            : resolveLatestExecution(data);
    }, [data, mode, executionId]);

    const resolvedMonth = useMemo(() => {
        if (data == null || mode !== 'month') {
            return null;
        }
        return resolveMonth(data, month);
    }, [data, mode, month]);

    if (isLoading) {
        return (
            <YStack width="100%" padding={24} alignItems="center">
                <Spinner />
            </YStack>
        );
    }

    if (isError || data == null) {
        return (
            <WidgetMessage message="Unable to load Income Split data right now." />
        );
    }

    if (mode === 'deposit') {
        if (resolvedDeposit == null) {
            return (
                <WidgetMessage message="No Income Split deposit found to show." />
            );
        }
        return <DepositDetail data={data} resolved={resolvedDeposit} />;
    }

    if (resolvedMonth == null) {
        return (
            <WidgetMessage message="No Income Split activity to show yet." />
        );
    }
    return (
        <IncomeSplitMonthView
            data={data}
            month={resolvedMonth}
            onPressExecution={onPressExecution}
        />
    );
};

export type IncomeSplitWidgetComponentProps = IncomeSplitWidgetProps &
    IncomeSplitWidgetDataProps;

export const IncomeSplitWidget = ({
    title,
    ...bodyProps
}: IncomeSplitWidgetComponentProps) => {
    return (
        <YStack width="100%">
            {title != null && title !== '' && (
                <YStack paddingHorizontal={16} paddingTop={16}>
                    <H5Emphasized color="$black">{title}</H5Emphasized>
                </YStack>
            )}
            <IncomeSplitWidgetBody {...bodyProps} />
        </YStack>
    );
};
